import { useContext, useEffect } from "react"
import { useDispatch } from "react-redux"
import type { ErrorSchema } from "@rjsf/core"
import { Footer, OutlinedButton } from "dash-styled"
import { SchemaFormRefProps } from "resha"
import { DocumentExtraction } from "universal-schema/lib/1.0.0/document-schema"
import { AnnotationFields } from "universal-schema/lib/1.0.0/annotation"
import { SchemaForm as SchemaFormType } from "universal-schema/lib"
import { ReactComponent as CheckCircleIcon } from "icons/lib/check_circle.svg"

import { getDetectedAnnotations } from "../../../../../Actions/API/VisualEditorActions"
import { InputFormRenderProps } from "../../../../Common/InputFormRender/InputFormRender"
import {
    DocumentExtractionData,
    DocumentType
} from "../../../../../Models/DataProgram/DocumentExtraction"
import { useAppState } from "../../../../../Hooks/useReduxState"
import { FieldModalType } from "../Common/Modals/FieldModal"
import { DocumentsExtractionContext } from "../DocumentsExtractionContext"
import { InputFormData } from ".."
import { DocumentsList } from "./Pane/DocumentsList"
import { DocumentRenderer } from "./Document/DocumentRenderer"
import {
    AnnotationFieldsStateContext,
    useAnnotationFieldsState
} from "./AnnotationFieldsStateContext"
import { BoundingBoxStatus } from "./constants"
import {
    checkFieldValid,
    convertToStyledDocedBoxFeature,
    getAnnotationDocumentExtraction
} from "./utils"

export type VisualEditorProps = {
    settings?: boolean
    demo?: {
        active: boolean
        toggle: () => void
    }
    input: InputFormRenderProps & React.RefAttributes<SchemaFormRefProps>
    output: Omit<InputFormRenderProps, "schema" | "uiSchema"> &
        React.RefAttributes<SchemaFormRefProps>
    parameterSchema: SchemaFormType | undefined
    errors?: ErrorSchema
    onBack?: () => void
    onForward?: () => void
    closeVisualEditor: () => void
    isOnboarding?: boolean
}

export const VisualEditor = ({
    input,
    output,
    onBack,
    onForward,
    parameterSchema,
    errors,
    closeVisualEditor,
    isOnboarding = false
}: VisualEditorProps) => {
    const dispatch = useDispatch()
    const detectedAnnotations = useAppState(
        state => state.api.visualEditor?.detectedAnnotations
    )
    const annotationFieldsState = useAnnotationFieldsState()

    const { signedUrl } = input.formData as InputFormData
    const {
        status,
        selectedId,
        visibleDocument,
        detectedBoundingBoxes,
        processedBoundingBoxes,
        processedAnnotations,
        setStatus,
        setVisibleDocument,
        openNewField,
        openFieldEdit,
        closeFieldModal,
        setCurrentBoundingBoxes,
        setDetectedBoundingBoxes,
        setProcessedBoundingBoxes,
        setDetectedAnnotations,
        setProcessedAnnotations,
        setCurrentAnnotations
    } = annotationFieldsState

    const { annotationsOutputList, appSchema, findOutputId } = useContext(
        DocumentsExtractionContext
    )

    useEffect(() => {
        if (detectedAnnotations) {
            const docedFeature = convertToStyledDocedBoxFeature(
                detectedAnnotations,
                BoundingBoxStatus.NON_TRUSTED
            )
            setDetectedBoundingBoxes(docedFeature)
            setDetectedAnnotations(
                detectedAnnotations.schema_instance.annotations
            )
        }
    }, [detectedAnnotations, setDetectedAnnotations, setDetectedBoundingBoxes])

    useEffect(() => {
        if (output.formData && annotationsOutputList && visibleDocument) {
            const annotations = annotationsOutputList.annotations
            if (annotations) {
                const docedFeature = convertToStyledDocedBoxFeature(
                    getAnnotationDocumentExtraction(annotations),
                    BoundingBoxStatus.TRUSTED
                )

                setProcessedBoundingBoxes(docedFeature)
                setProcessedAnnotations(annotations)
            }
        }
    }, [
        output.formData,
        annotationsOutputList,
        visibleDocument,
        setProcessedAnnotations,
        setProcessedBoundingBoxes
    ])

    useEffect(() => {
        if (signedUrl) {
            setVisibleDocument(signedUrl)
        }
    }, [signedUrl, setVisibleDocument])

    const handleSelectDetectedBoundingBox = (
        detectedAnnotations: DocumentExtraction
    ) => {
        Object.entries(detectedAnnotations.schema_instance.annotations).forEach(
            ([name, list]) => {
                //TODO cover key,value case
                const annotation = list.find((data, i) => {
                    const shapeId = detectedBoundingBoxes.shapesState[name]?.[i]
                    if (
                        shapeId !== undefined &&
                        typeof shapeId !== "string" &&
                        ("key" in shapeId || "value" in shapeId)
                    ) {
                        return (
                            shapeId.key === selectedId ||
                            shapeId.value === selectedId
                        )
                    }
                    return shapeId === selectedId
                })
                if (annotation) {
                    setCurrentAnnotations(list)
                    setStatus(BoundingBoxStatus.NON_TRUSTED)
                    setCurrentBoundingBoxes({
                        boundingBoxes:
                            detectedBoundingBoxes.boundingBoxes.filter(
                                ({ selection }) => selection.tag === name
                            ),
                        shapesState: {
                            [name]: detectedBoundingBoxes.shapesState[name]
                        }
                    })
                    openNewField()
                }
            }
        )
    }

    const handleSelectProcessedBoundingBox = (
        processedAnnotations: AnnotationFields
    ) => {
        Object.entries(processedAnnotations).forEach(([name, list]) => {
            const annotation = list.find((data, i) => {
                const shapeId = processedBoundingBoxes.shapesState[name]?.[i]
                if (
                    shapeId !== undefined &&
                    typeof shapeId !== "string" &&
                    ("key" in shapeId || "value" in shapeId)
                ) {
                    return (
                        shapeId.key === selectedId ||
                        shapeId.value === selectedId
                    )
                }
                return shapeId === selectedId
            })
            if (annotation) {
                const fieldIndex = (
                    output.formData as DocumentExtractionData
                )?.documents[0]?.annotationFields?.findIndex(
                    ({ fieldId }) => findOutputId(fieldId.data) === name
                )
                if (fieldIndex !== undefined && fieldIndex >= 0) {
                    setCurrentAnnotations(list)
                    setStatus(BoundingBoxStatus.TRUSTED)
                    setCurrentBoundingBoxes({
                        boundingBoxes:
                            processedBoundingBoxes.boundingBoxes.filter(
                                ({ selection }) => selection.tag === name
                            ),
                        shapesState: {
                            [name]: processedBoundingBoxes.shapesState[name]
                        }
                    })

                    openFieldEdit({
                        index: fieldIndex,
                        type: FieldModalType.Edit
                    })
                }
            }
        })
    }

    useEffect(() => {
        // should be triggered only by Doced module events
        if (selectedId && !status) {
            if (
                JSON.stringify(detectedBoundingBoxes.shapesState).includes(
                    selectedId
                )
            ) {
                // clicked on non-trusted annotation
                const selectedBoundingBox =
                    detectedBoundingBoxes.boundingBoxes.find(
                        ({ id }) => id === selectedId
                    )
                if (detectedAnnotations && selectedBoundingBox) {
                    handleSelectDetectedBoundingBox(detectedAnnotations)
                }
            } else if (
                JSON.stringify(processedBoundingBoxes.shapesState).includes(
                    selectedId
                )
            ) {
                // clicked on trusted annotation
                const selectedBoundingBox =
                    processedBoundingBoxes.boundingBoxes.find(
                        ({ id }) => id === selectedId
                    )
                if (processedAnnotations && selectedBoundingBox) {
                    handleSelectProcessedBoundingBox(processedAnnotations)
                }
            }
        } else {
            closeFieldModal()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId])

    const detectAnnotations = (doc: DocumentType) => {
        dispatch(getDetectedAnnotations(doc))
    }

    const handleChange = (data: DocumentExtractionData, type?: "document") => {
        output.onChange?.(data, [])

        if (type === "document") {
            detectAnnotations(data.documents[0])
        }
    }

    const allDocumentsAreValid = (
        output.formData as DocumentExtractionData
    )?.documents?.every(({ annotationFields }) =>
        annotationFields?.every(field =>
            checkFieldValid(
                findOutputId(field.fieldId.data),
                field,
                annotationsOutputList
            )
        )
    )

    return (
        <AnnotationFieldsStateContext.Provider value={annotationFieldsState}>
            <div
                className="flex w-full px-6 pt-6 pb-4 bg-smoke-200"
                style={{
                    height: `calc(100% - ${isOnboarding ? "70px" : "80px"} )`
                }}
            >
                <div className="w-3/5 pr-4">
                    <DocumentRenderer onBack={onBack} />
                </div>
                <div className="w-2/5 bg-smoke-200">
                    <div className="w-full h-full bg-white rounded-lg shadow-lg">
                        {parameterSchema !== undefined && (
                            <DocumentsList
                                newDocument={
                                    output.formData === undefined ||
                                    (output.formData as DocumentExtractionData)
                                        ?.documents?.length === 0
                                }
                                schema={parameterSchema.schema}
                                formData={
                                    output.formData as DocumentExtractionData
                                }
                                errors={errors}
                                onChange={handleChange}
                            />
                        )}
                    </div>
                </div>
            </div>
            <Footer text="" actions={<></>}>
                <OutlinedButton
                    label="Back to simple editor"
                    className="py-3 bg-white border-gray-500"
                    onClick={closeVisualEditor}
                />
                <OutlinedButton
                    disabled={
                        appSchema === "loading" ||
                        onForward === undefined ||
                        !allDocumentsAreValid
                    }
                    label={isOnboarding ? "Finish Setup" : "Save"}
                    Icon={CheckCircleIcon}
                    color="darkviolet"
                    variant="contained"
                    className="rounded-lg ml-3 py-3"
                    onClick={onForward}
                />
            </Footer>
        </AnnotationFieldsStateContext.Provider>
    )
}
